"use client"

import {
  navigateWithThreadPreservation,
  getPageHeader,
  isConversationalBriefEnabled,
  getPluginSlots,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PageHeaderActionPrimary,
  PageHeaderActions,
  PaneResponsiveCardGrid,
  DashboardSectionSkeleton,
  PaneListEmptyState,
  useArtifactCanvas,
  useListFilterBar,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useBriefs, useDeleteBrief } from '../../hooks/use-briefs'
import { BriefListCard } from './brief-list-card'
import { BriefCanvasForm } from './brief-canvas-form'
import { ConversationalBriefCreate } from './conversational-brief-create'
import {
  computeRecruiterBriefStatusCounts,
  RECRUITER_BRIEFS_FILTER_DEFINITIONS,
  recruiterBriefStatusLabel,
  type RecruiterBriefsFilterState,
} from '../../lib/list-filters/recruiter-briefs'
import { FileText, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

export function BriefsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [statusFilter, setStatusFilter] = useState<RecruiterBriefsFilterState['status']>('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [briefToDelete, setBriefToDelete] = useState<string | null>(null)
  const createCanvasOpenKeyRef = useRef(0)
  const { openCanvas, closeCanvas } = useArtifactCanvas()

  const { briefs: allBriefs, isLoading, error } = useBriefs()
  const { deleteBrief, isDeleting } = useDeleteBrief()

  const filterCounts = useMemo(() => computeRecruiterBriefStatusCounts(allBriefs), [allBriefs])
  const filterCtx = useMemo(() => ({ counts: filterCounts }), [filterCounts])

  const briefFilters: RecruiterBriefsFilterState = useMemo(
    () => ({ search: '', status: statusFilter }),
    [statusFilter],
  )

  const onFiltersChange = useCallback((next: RecruiterBriefsFilterState) => {
    setStatusFilter(next.status)
  }, [])

  const clearFilters = useCallback(() => {
    setStatusFilter('')
  }, [])

  const { filterBar } = useListFilterBar({
    listId: 'recruiterBriefs',
    definitions: RECRUITER_BRIEFS_FILTER_DEFINITIONS,
    filters: briefFilters,
    onFiltersChange,
    ctx: filterCtx,
    onClearAll: clearFilters,
  })

  const briefs = useMemo(() => {
    if (!statusFilter) return allBriefs
    return allBriefs.filter((b) => b.status === statusFilter)
  }, [allBriefs, statusFilter])

  useEffect(() => {
    return () => {
      closeCanvas()
    }
  }, [closeCanvas])

  useEffect(() => {
    if (statusFilter && filterCounts[statusFilter] === 0) {
      setStatusFilter('')
    }
  }, [statusFilter, filterCounts])

  const openCreateBriefCanvas = useCallback((mode: 'auto' | 'form' = 'auto') => {
    createCanvasOpenKeyRef.current += 1
    const conversational =
      mode === 'auto' && isConversationalBriefEnabled()
        ? getPluginSlots().conversationalBrief
        : null
    const onSaved = (created: { id: string }) => {
      closeCanvas()
      navigateWithThreadPreservation(navigate, `/recruiter/briefs/${created.id}`, searchParams)
    }
    openCanvas({
      title: conversational ? 'Describe the role' : 'Create brief',
      preventAutoFocus: true,
      content: conversational ? (
        <ConversationalBriefCreate
          key={`brief-create-${createCanvasOpenKeyRef.current}`}
          plugin={conversational}
          onClose={closeCanvas}
          onUseForm={() => {
            closeCanvas()
            openCreateBriefCanvas('form')
          }}
          onSaved={onSaved}
        />
      ) : (
        <BriefCanvasForm
          key={`brief-create-${createCanvasOpenKeyRef.current}`}
          mode="create"
          onClose={closeCanvas}
          onSaved={onSaved}
        />
      ),
    })
  }, [closeCanvas, navigate, openCanvas, searchParams])

  useEffect(() => {
    if (searchParams.get('create') !== '1') return
    openCreateBriefCanvas()
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    setSearchParams(next, { replace: true })
  }, [openCreateBriefCanvas, searchParams, setSearchParams])

  const handleDelete = (briefId: string) => {
    setBriefToDelete(briefId)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (briefToDelete) {
      deleteBrief(briefToDelete, {
        onSuccess: () => {
          toast.success('Brief deleted successfully')
          setDeleteDialogOpen(false)
          setBriefToDelete(null)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to delete brief')
        },
      })
    }
  }

  if (error) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <p className="text-destructive">Error loading briefs: {(error as Error).message}</p>
        </div>
      </PageContainer>
    )
  }

  const { title } = getPageHeader('/recruiter/briefs', 'Briefs', 'Manage your recruitment briefs and candidate searches')

  return (
    <PageContainer>
        <PageHeaderStack>
          <PageHeader
            leading={<PageHeaderLeading />}
            title={title}
            icon={<FileText className="h-5 w-5" />}
            actions={
              <PageHeaderActions
                primary={
                  <PageHeaderActionPrimary type="button" onClick={() => openCreateBriefCanvas()}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Create Brief
                  </PageHeaderActionPrimary>
                }
              />
            }
          />

          {filterBar}
        </PageHeaderStack>

        {isLoading ? (
          <DashboardSectionSkeleton cards={4} grid />
        ) : briefs.length === 0 ? (
          <Card>
            <CardContent>
              <PaneListEmptyState
                icon={<FileText aria-hidden />}
                title="No briefs yet"
                description={
                  statusFilter
                    ? `No ${recruiterBriefStatusLabel(statusFilter)}`
                    : 'Create a brief to start searching for candidates'
                }
                action={
                  statusFilter ? (
                    <Button type="button" variant="outline" onClick={() => setStatusFilter('')}>
                      Show all briefs
                    </Button>
                  ) : (
                    <Button type="button" onClick={() => openCreateBriefCanvas()}>
                      <Plus className="mr-2 size-4" aria-hidden />
                      Create Brief
                    </Button>
                  )
                }
              />
            </CardContent>
          </Card>
        ) : (
          <PaneResponsiveCardGrid>
            {briefs.map((brief) => (
              <BriefListCard
                key={brief.id}
                brief={brief}
                className="h-full"
                onOpen={() =>
                  navigateWithThreadPreservation(navigate, `/recruiter/briefs/${brief.id}`, searchParams)
                }
                trailingActions={
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(brief.id)}>
                    <Trash2 className="size-4" />
                  </Button>
                }
              />
            ))}
          </PaneResponsiveCardGrid>
        )}

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete brief</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this brief? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
</PageContainer>
  )
}
